import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';

const WRAPPER_SELECTOR = '[data-component="product-reviews"]';
const LIST_SELECTOR = '[data-product-reviews-list]';
const PAGINATION_SELECTOR = '[data-product-reviews-pagination]';
const PAGE_BUTTON_SELECTOR = '[data-product-reviews-page]';
const SLIDER_SELECTOR = '[data-product-reviews-slider]';
const SLIDER_PAGINATION_SELECTOR = '[data-product-reviews-slider-pagination]';
const SHOW_ALL_SELECTOR = '[data-product-reviews-show-all]';
const SHOW_ALL_LABEL_SELECTOR = '[data-product-reviews-show-all-label]';
const OPEN_MODAL_SELECTOR = '[data-product-reviews-open-modal]';
const CLOSE_MODAL_SELECTOR = '[data-product-reviews-close-modal]';
const MODAL_SELECTOR = '[data-product-reviews-modal]';
const STAR_LINK_SELECTOR = '#respond p.stars a';
const STARS_SELECTOR = '#respond p.stars';

const parsePositiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
};

const normalizeRatingValue = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1 || parsed > 5) {
        return 0;
    }

    return parsed;
};

const parseStarRatingFromClass = (starElement) => {
    if (!starElement) {
        return 0;
    }

    const starClass = Array.from(starElement.classList).find((className) => /^star-[1-5]$/.test(className));

    if (!starClass) {
        return 0;
    }

    return normalizeRatingValue(starClass.replace('star-', ''));
};

const applyStarsRatingState = (starsWrapper, ratingValue) => {
    if (!starsWrapper) {
        return;
    }

    const normalizedRating = normalizeRatingValue(ratingValue);
    const starLinks = starsWrapper.querySelectorAll('a');

    starsWrapper.setAttribute('role', 'radiogroup');
    starsWrapper.setAttribute('aria-labelledby', 'comment-form-rating-label');

    if (normalizedRating > 0) {
        starsWrapper.dataset.rating = String(normalizedRating);
    } else {
        delete starsWrapper.dataset.rating;
    }

    starsWrapper.classList.toggle('selected', normalizedRating > 0);

    starLinks.forEach((starLink) => {
        const starValue = parseStarRatingFromClass(starLink);
        const isActive = normalizedRating > 0 && starValue === normalizedRating;

        starLink.setAttribute('role', 'radio');
        starLink.classList.toggle('active', isActive);
        starLink.setAttribute('aria-checked', isActive ? 'true' : 'false');
        starLink.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    if (normalizedRating === 0 && starLinks.length) {
        starLinks[0].setAttribute('tabindex', '0');
    }
};

const synchronizeComponentStars = (component) => {
    const starsWrappers = component.querySelectorAll(STARS_SELECTOR);

    starsWrappers.forEach((starsWrapper) => {
        const respond = starsWrapper.closest('#respond');
        const ratingSelect = respond ? respond.querySelector('#rating') : null;
        const ratingValue = ratingSelect ? ratingSelect.value : '';

        applyStarsRatingState(starsWrapper, ratingValue);
    });
};

const setLoadingState = (component, isLoading) => {
    component.classList.toggle('is-loading', isLoading);
    component.setAttribute('aria-busy', String(isLoading));
};

const destroyReviewsSlider = (component) => {
    const slider = component.querySelector(SLIDER_SELECTOR);

    if (slider && slider.swiper) {
        slider.swiper.destroy(true, true);
    }

    component.classList.remove('is-slider-ready');
};

const initializeReviewsSlider = (component) => {
    const slider = component.querySelector(SLIDER_SELECTOR);
    const wrapper = slider ? slider.querySelector('.swiper-wrapper') : null;

    if (!slider || !wrapper || component.classList.contains('is-expanded')) {
        component.classList.remove('is-slider-ready');
        return null;
    }

    if (slider.swiper) {
        slider.swiper.destroy(true, true);
    }

    const pagination = component.querySelector(SLIDER_PAGINATION_SELECTOR);
    const instance = new Swiper(slider, {
        modules: [Pagination],
        slidesPerView: 'auto',
        spaceBetween: 16,
        grabCursor: true,
        observer: true,
        observeParents: true,
        roundLengths: true,
        threshold: 5,
        watchOverflow: true,
        pagination: pagination
            ? {
                  el: pagination,
                  clickable: true,
              }
            : undefined,
    });

    component.classList.add('is-slider-ready');

    return instance;
};

const fetchReviewsPage = async (component, pageNumber) => {
    if (!window.jsData || !window.jsData.ajaxUrl) {
        throw new Error('Missing jsData.ajaxUrl');
    }

    const productId = parsePositiveInteger(component.dataset.productId, 0);
    const action = component.dataset.action;
    const nonce = component.dataset.nonce || '';

    if (!productId || !action || !nonce) {
        throw new Error('Missing required payload values');
    }

    const payload = new URLSearchParams();
    payload.append('action', action);
    payload.append('product_id', String(productId));
    payload.append('page', String(pageNumber));
    payload.append('nonce', nonce);

    const response = await fetch(window.jsData.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: payload.toString(),
    });

    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
    }

    return response.json();
};

const replaceReviewsMarkup = (component, responseData) => {
    const list = component.querySelector(LIST_SELECTOR);
    const pagination = component.querySelector(PAGINATION_SELECTOR);

    destroyReviewsSlider(component);

    if (list && typeof responseData.list_html === 'string') {
        list.innerHTML = responseData.list_html;
    }

    if (pagination && typeof responseData.pagination_html === 'string') {
        pagination.innerHTML = responseData.pagination_html;
    }

    const currentPage = parsePositiveInteger(responseData.current_page, 1);
    component.dataset.currentPage = String(currentPage);

    if (!component.classList.contains('is-expanded')) {
        initializeReviewsSlider(component);
    }
};

const bindReviewsPagination = (component) => {
    let isRequestPending = false;

    component.addEventListener('click', async (event) => {
        const target = event.target.closest(PAGE_BUTTON_SELECTOR);

        if (!target) {
            return;
        }

        event.preventDefault();

        if (target.hasAttribute('disabled') || isRequestPending) {
            return;
        }

        const nextPage = parsePositiveInteger(target.dataset.productReviewsPage, 0);
        const currentPage = parsePositiveInteger(component.dataset.currentPage, 1);

        if (!nextPage || nextPage === currentPage) {
            return;
        }

        isRequestPending = true;
        setLoadingState(component, true);

        try {
            const response = await fetchReviewsPage(component, nextPage);

            if (!response || response.success !== true || !response.data) {
                return;
            }

            replaceReviewsMarkup(component, response.data);
        } catch (error) {
            // Keep graceful degradation: on request failure the currently rendered
            // opinions remain visible and interactive.
            console.error(error);
        } finally {
            isRequestPending = false;
            setLoadingState(component, false);
        }
    });
};

const bindReviewsShowAll = (component) => {
    const button = component.querySelector(SHOW_ALL_SELECTOR);

    if (!button) {
        return;
    }

    const label = button.querySelector(SHOW_ALL_LABEL_SELECTOR);

    button.addEventListener('click', () => {
        const expanded = !component.classList.contains('is-expanded');

        component.classList.toggle('is-expanded', expanded);
        button.setAttribute('aria-expanded', String(expanded));

        if (label) {
            label.textContent = expanded
                ? button.dataset.labelExpanded || ''
                : button.dataset.labelCollapsed || '';
        }

        if (expanded) {
            destroyReviewsSlider(component);
            return;
        }

        initializeReviewsSlider(component);
    });
};

const getFocusableElements = (container) =>
    Array.from(
        container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

const bindReviewsModal = (component) => {
    const modal = component.querySelector(MODAL_SELECTOR);

    if (!modal) {
        return;
    }

    const openButtons = component.querySelectorAll(OPEN_MODAL_SELECTOR);
    const closeButtons = component.querySelectorAll(CLOSE_MODAL_SELECTOR);
    const modalInner = modal.querySelector('.ProductReviewsModal__inner');
    let lastFocusedElement = null;
    let previousOverflow = '';

    const closeModal = (event) => {
        if (event) {
            event.preventDefault();
        }

        modal.classList.remove('open');
        modal.setAttribute('aria-hidden', 'true');
        modal.setAttribute('inert', '');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        document.documentElement.style.overflow = previousOverflow;

        if (
            lastFocusedElement
            && typeof lastFocusedElement.focus === 'function'
            && lastFocusedElement.isConnected
        ) {
            lastFocusedElement.focus();
        }
    };

    const openModal = (event) => {
        if (event) {
            event.preventDefault();
        }

        lastFocusedElement = document.activeElement;
        previousOverflow = document.documentElement.style.overflow;
        modal.removeAttribute('inert');
        modal.classList.add('open');
        modal.setAttribute('aria-hidden', 'false');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        document.documentElement.style.overflow = 'hidden';

        window.requestAnimationFrame(() => {
            if (modalInner) {
                modalInner.focus();
            }
        });
    };

    openButtons.forEach((button) => {
        button.addEventListener('click', openModal);
    });

    closeButtons.forEach((button) => {
        button.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (event) => {
        const clickedInsideInner = modalInner && modalInner.contains(event.target);

        if (!clickedInsideInner) {
            closeModal(event);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (!modal.classList.contains('open')) {
            return;
        }

        if (event.key === 'Escape') {
            closeModal();
            return;
        }

        if (event.key !== 'Tab' || !modalInner) {
            return;
        }

        const focusableElements = getFocusableElements(modalInner);

        if (!focusableElements.length) {
            event.preventDefault();
            modalInner.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === modalInner)) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    });
};

const bindReviewsRating = (component) => {
    component.addEventListener(
        'click',
        (event) => {
            const starLink = event.target.closest(STAR_LINK_SELECTOR);

            if (!starLink || !component.contains(starLink)) {
                return;
            }

            const starsWrapper = starLink.closest('p.stars');
            const ratingValue = parseStarRatingFromClass(starLink);

            if (!starsWrapper || ratingValue === 0) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const respond = starLink.closest('#respond');
            const ratingSelect = respond ? respond.querySelector('#rating') : null;

            if (ratingSelect) {
                ratingSelect.value = String(ratingValue);
                ratingSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }

            applyStarsRatingState(starsWrapper, ratingValue);
        },
        true,
    );

    synchronizeComponentStars(component);

    if (typeof MutationObserver === 'undefined') {
        return;
    }

    const observer = new MutationObserver((mutations) => {
        const shouldSync = mutations.some((mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0);

        if (shouldSync) {
            synchronizeComponentStars(component);
        }
    });

    observer.observe(component, {
        childList: true,
        subtree: true,
    });
};

document.addEventListener('DOMContentLoaded', () => {
    const reviewComponents = document.querySelectorAll(WRAPPER_SELECTOR);

    reviewComponents.forEach((component) => {
        initializeReviewsSlider(component);
        bindReviewsShowAll(component);
        bindReviewsRating(component);
        bindReviewsPagination(component);
        bindReviewsModal(component);
    });
});
